import type {
  BlockedReason,
  OpKey,
  OperationKind,
  PendingOp,
  StaleReason,
  TemplatePermissions,
} from './operations';

export type OperationVerdict =
  | { allowed: true }
  | { allowed: false; reason: BlockedReason };

export type PolicyContext = {
  pendingOps: Readonly<Record<OpKey, PendingOp>>;
  staleReason: StaleReason | null;
  permissions: TemplatePermissions;
};

function isPermitted(
  kind: OperationKind,
  permissions: TemplatePermissions
): boolean {
  switch (kind) {
    case 'hideSection':
    case 'unhideSection':
    case 'hideQuestion':
    case 'unhideQuestion':
      return permissions.canManage;
    case 'reorder':
      return permissions.canReorder;
  }
}

// A reorder and a hide or unhide never overlap, whatever their targets
function findExclusion(
  kind: OperationKind,
  pendingOps: PolicyContext['pendingOps']
): BlockedReason | null {
  const pendingKinds = Object.values(pendingOps).map(op => op.kind);

  if (kind === 'reorder') {
    return pendingKinds.some(pendingKind => pendingKind !== 'reorder')
      ? 'MUTATION_IN_FLIGHT'
      : null;
  }

  return pendingKinds.includes('reorder') ? 'REORDER_IN_FLIGHT' : null;
}

function hasOperationOnTarget(
  targetId: string,
  pendingOps: PolicyContext['pendingOps']
): boolean {
  return Object.values(pendingOps).some(op => op.targetId === targetId);
}

// Static reasons come before transient ones: waiting never clears them
export function checkOperationPreconditions(
  kind: OperationKind,
  ctx: PolicyContext
): OperationVerdict {
  if (!isPermitted(kind, ctx.permissions)) {
    return { allowed: false, reason: 'NOT_PERMITTED' };
  }

  if (ctx.staleReason !== null) {
    return { allowed: false, reason: 'REPLICA_STALE' };
  }

  const exclusion = findExclusion(kind, ctx.pendingOps);

  if (exclusion) {
    return { allowed: false, reason: exclusion };
  }

  return { allowed: true };
}

// A pending op on a different target is allowed, the mutation lane runs those in order
export function isOperationAllowed(
  kind: OperationKind,
  targetId: string,
  ctx: PolicyContext
): OperationVerdict {
  const verdict = checkOperationPreconditions(kind, ctx);

  if (!verdict.allowed) {
    return verdict;
  }

  if (hasOperationOnTarget(targetId, ctx.pendingOps)) {
    return { allowed: false, reason: 'SAME_TARGET_IN_FLIGHT' };
  }

  return verdict;
}
